import {
  anchorPosition,
  formatMeasurement,
  type Evaluated,
  angleMarkNominal,
} from '@/domain/construction';
import { formulaLabel } from '@/domain/expression';
import { type Vec2, vec } from '@/domain/math';
import { LineStyle, PointStyle, type Style } from '@/domain/model';
import { type SolidCurve } from '@/domain/solid';
import { type Scene } from '@/engine';

import {
  angleMarkGeometry,
  rightAngleCorners,
  tickRadii,
  valuePosition,
} from '../painters/angleMark';
import { axisStroke, nameInk, numberInk, tickStroke } from '../painters/axesStyle';
import { clipLineToBounds, clipRayToBounds } from '../painters/clip';
import { dashedIfCovered } from '../painters/hiddenLines';
import { DEFAULT_NAME_DIRECTION } from '../painters/namePlacement';
import {
  formatDegrees,
  LABEL_FONT,
  angleValueSize,
  labelSize,
  NAME_GAP,
  namePlacement,
} from '../painters/paintValue';
import { approximateMeasurer, layoutText } from '../text';

import {
  type Drawable,
  drawList,
  exportFrame,
  type ExportFrame,
  type FrameOptions,
  projectorFor,
  type ContentOptions,
} from './drawList';

/**
 * SVG export.
 *
 * Pure: a document in, a string out, no DOM. That is what makes it testable at all — the
 * assertions read the emitted markup directly, which is the only way to know a fill rule or
 * a dash pattern actually reached the file rather than merely being intended.
 *
 * ## Why not serialise the canvas
 *
 * A raster of the canvas would be far less code. It would also defeat the point: a teacher
 * exporting SVG wants a *vector* diagram to drop into a worksheet and scale to any size, and
 * an editor whose SVG is a screenshot is one that fails the moment somebody prints it.
 *
 * ## Screen-space widths become world-space widths
 *
 * On the canvas, stroke widths and point sizes are constant in **screen pixels** — a point
 * stays clickable at every zoom. An export has no zoom to be constant against, so those
 * lengths are emitted in output units directly. A 1.5-pixel stroke becomes a 1.5-unit stroke
 * at scale 1, which is what makes an SVG scaled to twice the size still look like the same
 * drawing rather than one with hairlines.
 */

export interface SvgOptions extends FrameOptions, ContentOptions {
  /** Page background. Omit for a transparent SVG, which is usually what a worksheet wants. */
  readonly background?: string;
  /** Written into the `<title>` element, where a screen reader will read it. */
  readonly title?: string;
}

export function toSvg(scene: Scene, options: SvgOptions = {}): string {
  const drawables = drawList(scene, options.only);
  const frame = exportFrame(drawables, options);

  const body: string[] = [];
  const hatches = new Set<string>();
  for (const drawable of drawables) {
    if (drawable.style.fillStyle === 'hatch' && drawable.style.fill !== 'transparent') {
      hatches.add(drawable.style.fill);
    }
  }

  if (options.background !== undefined) {
    body.push(
      `<rect x="0" y="0" width="${num(frame.width)}" height="${num(frame.height)}" fill="${escapeAttribute(options.background)}"/>`
    );
  }

  for (const drawable of drawables) {
    body.push(...elementsFor(drawable, frame));

    // The name is drawn after the object it names, so a thick stroke never covers it — and
    // it is drawn here at all so that an export shows what the screen showed.
    if (drawable.label !== undefined) {
      const at = anchorPosition(drawable.value);
      if (at !== null) {
        body.push(
          nameElement(
            projectorFor(frame)(at),
            drawable.nameDirection,
            drawable.label,
            drawable.style,
            frame.scale
          )
        );
      }
    }
  }

  const title = options.title === undefined ? '' : `<title>${escapeText(options.title)}</title>`;

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<svg xmlns="http://www.w3.org/2000/svg" width="${num(frame.width)}" height="${num(frame.height)}" viewBox="0 0 ${num(frame.width)} ${num(frame.height)}">`,
    title,
    hatchDefs(hatches),
    ...body,
    '</svg>',
    '',
  ]
    .filter((line) => line !== '')
    .join('\n');
}

/**
 * One `<pattern>` per hatched colour, or nothing when the drawing has none.
 *
 * Forty-five degrees at {@link HATCH_SPACING}, matching the canvas so a figure looks the same in
 * the window and on the page — which is the whole point of emitting it at all. The tile is drawn
 * with two extra strokes above and below its own box so the diagonals meet across tile edges
 * instead of showing a seam every seven units.
 *
 * `userSpaceOnUse`, so the density is in drawing units and does not change with the shape's size.
 * A pattern anchored to the shape would give a small plane coarse stripes and a large one fine
 * ones, which reads as two different notations.
 */
function hatchDefs(colours: ReadonlySet<string>): string {
  if (colours.size === 0) return '';

  const step = HATCH_SPACING;
  const tiles = [...colours].map((colour) => {
    const stroke = escapeAttribute(colour);
    return (
      `<pattern id="${hatchId(colour)}" patternUnits="userSpaceOnUse" ` +
      `width="${num(step)}" height="${num(step)}">` +
      `<path d="M -${num(step)} ${num(step)} L ${num(step)} -${num(step)} ` +
      `M 0 ${num(step)} L ${num(step)} 0 ` +
      `M 0 ${num(2 * step)} L ${num(2 * step)} 0" ` +
      `stroke="${stroke}" stroke-width="1" fill="none"/>` +
      `</pattern>`
    );
  });

  return `<defs>${tiles.join('')}</defs>`;
}

/** A stable id per colour, so two shapes hatched alike share one pattern. */
function hatchId(colour: string): string {
  return `es-hatch-${colour.replace(/[^0-9a-z]/gi, '')}`;
}

/** Between hatch lines, in drawing units. The canvas uses the same number in screen pixels. */
const HATCH_SPACING = 7;

/* ---------------------------------------------------------------------- elements */

function elementsFor(drawable: Drawable, frame: ExportFrame): readonly string[] {
  const { value, style } = drawable;
  const to = projectorFor(frame);

  switch (value.kind) {
    case 'point':
      return [pointElement(to(value.point), style, frame.scale)];

    case 'segment': {
      const a = to(value.segment.a);
      const b = to(value.segment.b);
      return [
        polylineElement([a, b], false, dashedIfCovered(style, drawable.covered), frame.scale),
        // Heads the style asked for, on the order the segment already has. See the canvas painter.
        ...(style.arrows === 'start' || style.arrows === 'both'
          ? [arrowheadElement(b, a, style, frame.scale)]
          : []),
        ...(style.arrows === 'end' || style.arrows === 'both'
          ? [arrowheadElement(a, b, style, frame.scale)]
          : []),
      ];
    }

    case 'vector':
      return vectorElements(to(value.from), to(value.to), style, frame.scale);

    case 'line':
    case 'ray': {
      // Unbounded geometry is clipped to the frame, exactly as the canvas painter clips it
      // to the viewport. A drawing of a line is always a drawing of part of a line.
      const clipped = clipUnbounded(value, frame);
      if (clipped === null) return [];

      const drawn = polylineElement([to(clipped.a), to(clipped.b)], false, style, frame.scale);
      // A ray keeps its arrowhead on paper. See the note in the canvas painter: without it a
      // tia and a đoạn thẳng print as the same picture.
      return value.kind === 'line'
        ? [drawn]
        : [drawn, arrowheadElement(to(clipped.a), to(clipped.b), style, frame.scale)];
    }

    case 'circle': {
      const centre = to(value.circle.center);
      return [
        `<circle cx="${num(centre.x)}" cy="${num(centre.y)}" r="${num(value.circle.radius * frame.scale)}" ${paintAttributes(style, frame.scale)}/>`,
      ];
    }

    case 'ellipse': {
      const centre = to(value.ellipse.center);
      // SVG rotates clockwise about the element's centre; the world rotates anticlockwise,
      // and the y flip in `projectorFor` is what reverses the sign.
      const degrees = (-value.ellipse.rotation * 180) / Math.PI;
      return [
        `<ellipse cx="${num(centre.x)}" cy="${num(centre.y)}" rx="${num(value.ellipse.rx * frame.scale)}" ry="${num(value.ellipse.ry * frame.scale)}" transform="rotate(${num(degrees)} ${num(centre.x)} ${num(centre.y)})" ${paintAttributes(style, frame.scale)}/>`,
      ];
    }

    case 'arc':
      return [arcElement(value.arc, frame, style)];

    /*
     * Filled once, outlined edge by edge when the edges disagree about being covered.
     *
     * A section through a solid has an edge lying in a face the reader can see and another
     * running through the middle of the block. One outline with one dash state draws the second
     * like the first, and the print loses the cue the screen has.
     */
    case 'polygon': {
      const screen = value.vertices.map(to);
      if (drawable.coveredEdges === undefined && drawable.liftedEdges === undefined) {
        return [polylineElement(screen, true, style, frame.scale)];
      }
      const region = polylineElement(screen, true, { ...style, stroke: 'none' }, frame.scale);
      return [
        region,
        ...screen.flatMap((point, index) => {
          // A side handed over to a segment of its own is not written again here.
          if (drawable.liftedEdges?.[index] === true) return [];
          const next = screen[(index + 1) % screen.length] ?? point;
          return [
            polylineElement(
              [point, next],
              false,
              // An open polyline is never filled, so the region is drawn once above and each
              // edge only strokes.
              dashedIfCovered(style, drawable.coveredEdges?.[index]),
              frame.scale
            ),
          ];
        }),
      ];
    }

    case 'polyline':
      return [polylineElement(value.points.map(to), value.closed, style, frame.scale)];

    case 'axes': {
      // Three weights rather than one — see `axesStyle`, where the ordering is argued.
      const axis = axisStroke(style);
      const tick = tickStroke(style);
      const number = numberInk(style);
      const name = nameInk(style);

      return [
        // Ticks first, so an axis is never drawn under the marks that hang off it.
        ...value.ticks.map((segment) =>
          polylineElement([to(segment.a), to(segment.b)], false, tick, frame.scale)
        ),
        ...[value.xAxis, value.yAxis].map((segment) =>
          polylineElement([to(segment.a), to(segment.b)], false, axis, frame.scale)
        ),
        arrowheadElement(to(value.origin), to(value.xAxis.b), axis, frame.scale),
        arrowheadElement(to(value.origin), to(value.yAxis.b), axis, frame.scale),
        ...value.marks.map((mark) => textElement(to(mark.at), mark.text, number, frame.scale)),
        ...value.names.map((label) => textElement(to(label.at), label.text, name, frame.scale)),
      ];
    }

    // Dashed guides whatever the style says: a solid line to the axis reads as part of the
    // figure rather than as "read this off". See the canvas painter.
    case 'graph-marks':
      return [
        ...value.rules.map((rule) =>
          polylineElement(
            [to(rule.a), to(rule.b)],
            false,
            { ...style, lineStyle: LineStyle.Dashed },
            frame.scale
          )
        ),
        ...value.points.map((point) => pointElement(to(point), style, frame.scale)),
        ...value.marks.map((mark) =>
          centredTextElement(to(mark.at), mark.text, style, frame.scale)
        ),
      ];

    case 'variation-table':
      return [
        ...value.rules.map((rule) =>
          polylineElement([to(rule.a), to(rule.b)], false, style, frame.scale)
        ),
        ...value.marks.map((mark) =>
          centredTextElement(to(mark.at), mark.text, style, frame.scale)
        ),
      ];

    // One element per continuous piece — an SVG `polyline` cannot express a gap.
    /*
     * The curve, and the formula beside it.
     *
     * The formula was drawn on the canvas and by neither exporter, so a graph printed or dropped
     * into a worksheet arrived with nothing saying what it was — the same drift that lost the
     * names out of raster exports. It is taken from `labelAt`, which the evaluator places, so all
     * three put it in one spot and the hit test agrees with all of them.
     */
    case 'function-graph': {
      const curve = value.segments.map((segment) =>
        polylineElement(segment.map(to), false, style, frame.scale)
      );
      const label = formulaLabel(value.expression);
      if (label === null || value.labelAt === null) return curve;
      return [...curve, textElement(to(value.labelAt), label, style, frame.scale)];
    }

    // Nothing is drawn for a viewpoint, in any format. The entity is invisible too, so the
    // exporter never reaches here — the case exists so that adding a kind stays a compile error.
    case 'space':
      return [];

    case 'solid':
      return value.curves !== undefined && value.curves.length > 0
        ? solidCurveElements(value.curves, value.straight, to, style, frame)
        : solidElements(value.visible, value.hidden, to, style, frame.scale);

    case 'angle-mark':
      return angleMarkElements(value, to, style, frame.scale);

    case 'text':
      return [textElement(to(value.position), value.content, style, frame.scale)];

    case 'measurement':
      return [textElement(to(value.position), formatMeasurement(value.value), style, frame.scale)];

    /*
     * Embedded whole, because the href already *is* the bytes.
     *
     * This was the note that said images could not be exported — and the reason was true of
     * the design as it stood, where an image was a reference to something outside the file.
     * Storing pictures as `data:` URIs removed the problem rather than solving it: an SVG
     * carrying the URI is as self-contained as the document it came from.
     */
    case 'image': {
      // The world's y axis points up, SVG's points down, so the top-left corner in screen
      // terms is the *upper* world corner.
      const corner = to(vec.vec2(value.position.x, value.position.y + value.height));
      return [
        `<image x="${num(corner.x)}" y="${num(corner.y)}" width="${num(value.width * frame.scale)}" height="${num(value.height * frame.scale)}" href="${escapeAttribute(value.href)}" preserveAspectRatio="none"/>`,
      ];
    }
  }
}

function pointElement(centre: Vec2, style: Style, scale: number): string {
  const r = (style.pointSize / 2) * scale;
  const fill = escapeAttribute(style.stroke);

  switch (style.pointStyle) {
    case PointStyle.Dot:
      return `<circle cx="${num(centre.x)}" cy="${num(centre.y)}" r="${num(r)}" fill="${fill}"/>`;

    case PointStyle.Circle:
      return `<circle cx="${num(centre.x)}" cy="${num(centre.y)}" r="${num(r)}" fill="none" stroke="${fill}" stroke-width="${num(style.strokeWidth * scale)}"/>`;

    case PointStyle.Square:
      return `<rect x="${num(centre.x - r)}" y="${num(centre.y - r)}" width="${num(r * 2)}" height="${num(r * 2)}" fill="${fill}"/>`;

    case PointStyle.Cross:
      return `<path d="M ${num(centre.x - r)} ${num(centre.y - r)} L ${num(centre.x + r)} ${num(centre.y + r)} M ${num(centre.x + r)} ${num(centre.y - r)} L ${num(centre.x - r)} ${num(centre.y + r)}" stroke="${fill}" stroke-width="${num(style.strokeWidth * scale)}" fill="none"/>`;

    case PointStyle.Plus:
      return `<path d="M ${num(centre.x - r)} ${num(centre.y)} L ${num(centre.x + r)} ${num(centre.y)} M ${num(centre.x)} ${num(centre.y - r)} L ${num(centre.x)} ${num(centre.y + r)}" stroke="${fill}" stroke-width="${num(style.strokeWidth * scale)}" fill="none"/>`;
  }
}

function polylineElement(
  points: readonly Vec2[],
  closed: boolean,
  style: Style,
  scale: number
): string {
  if (points.length === 0) return '';
  const path = pathData(points, closed);
  return `<path d="${path}" ${paintAttributes(style, scale, closed)}/>`;
}

function vectorElements(from: Vec2, to: Vec2, style: Style, scale: number): readonly string[] {
  const shaft = polylineElement([from, to], false, style, scale);

  // The arrowhead is drawn, not referenced through a marker: a marker inherits the path's
  // stroke in some renderers and not others, and a vector whose head is the wrong colour in
  // half the world's viewers is worse than a few extra bytes.
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-9) return [shaft];

  const size = Math.max(6, style.strokeWidth * 4) * scale;
  const ux = dx / length;
  const uy = dy / length;
  const spread = 0.42;

  const left = {
    x: to.x - size * (ux * Math.cos(spread) - uy * Math.sin(spread)),
    y: to.y - size * (uy * Math.cos(spread) + ux * Math.sin(spread)),
  };
  const right = {
    x: to.x - size * (ux * Math.cos(spread) + uy * Math.sin(spread)),
    y: to.y - size * (uy * Math.cos(spread) - ux * Math.sin(spread)),
  };

  return [
    shaft,
    `<path d="M ${num(to.x)} ${num(to.y)} L ${num(left.x)} ${num(left.y)} L ${num(right.x)} ${num(right.y)} Z" fill="${escapeAttribute(style.stroke)}" opacity="${num(style.opacity)}"/>`,
  ];
}

function arcElement(
  arc: {
    center: Vec2;
    radius: number;
    startAngle: number;
    endAngle: number;
    counterclockwise?: boolean;
  },
  frame: ExportFrame,
  style: Style
): string {
  const to = projectorFor(frame);
  const at = (angle: number): Vec2 =>
    to({
      x: arc.center.x + Math.cos(angle) * arc.radius,
      y: arc.center.y + Math.sin(angle) * arc.radius,
    });

  const start = at(arc.startAngle);
  const end = at(arc.endAngle);
  const radius = arc.radius * frame.scale;

  let sweep = arc.endAngle - arc.startAngle;
  while (sweep < 0) sweep += Math.PI * 2;
  const largeArc = sweep > Math.PI ? 1 : 0;

  // Sweep flag 0, not 1: the y flip reverses the direction of travel, so an arc that runs
  // anticlockwise in the world runs clockwise on the page.
  return `<path d="M ${num(start.x)} ${num(start.y)} A ${num(radius)} ${num(radius)} 0 ${String(largeArc)} 0 ${num(end.x)} ${num(end.y)}" ${paintAttributes(style, frame.scale)}/>`;
}

/** One drawn edge of a solid, on the page. */
interface Edge {
  readonly a: Vec2;
  readonly b: Vec2;
}

/**
 * A round solid written as the conics it is: an `<ellipse>` for a whole rim or a sphere's
 * outline, an arc path for each half. Vector all the way to the page, so a printed figure is
 * round however far it is enlarged — which a chain of chords never is, however many there are.
 *
 * A cylinder brings straight sides along with its rims, and they are emitted in the same
 * hidden-then-visible order so that nothing dashed is laid over something solid.
 */
function solidCurveElements(
  curves: readonly SolidCurve[],
  straight: { visible: readonly Edge[]; hidden: readonly Edge[] } | undefined,
  to: (point: Vec2) => Vec2,
  style: Style,
  frame: ExportFrame
): readonly string[] {
  // SVG turns clockwise about the element's centre; the world turns anticlockwise, and the y
  // flip in `projectorFor` is what reverses the sign.
  const degrees = (radians: number) => (-radians * 180) / Math.PI;

  const elementFor = (curve: SolidCurve): string => {
    const { ellipse } = curve;
    const centre = to(ellipse.center);
    const rx = ellipse.rx * frame.scale;
    const ry = ellipse.ry * frame.scale;
    const attributes = curve.hidden
      ? `fill="none" stroke="${escapeAttribute(style.stroke)}" ` +
        `stroke-width="${num(Math.max(0.5, style.strokeWidth * 0.85) * frame.scale)}" ` +
        `stroke-dasharray="${num(5 * frame.scale)} ${num(4 * frame.scale)}" ` +
        `opacity="${num(style.opacity * 0.45)}"`
      : paintAttributes(style, frame.scale);

    if (curve.from === undefined || curve.sweep === undefined) {
      return (
        `<ellipse cx="${num(centre.x)}" cy="${num(centre.y)}" rx="${num(rx)}" ry="${num(ry)}" ` +
        `transform="rotate(${num(degrees(ellipse.rotation))} ${num(centre.x)} ${num(centre.y)})" ` +
        `${attributes}/>`
      );
    }

    const at = (t: number): Vec2 => {
      const cos = Math.cos(ellipse.rotation);
      const sin = Math.sin(ellipse.rotation);
      const x = ellipse.rx * Math.cos(t);
      const y = ellipse.ry * Math.sin(t);
      return to({
        x: ellipse.center.x + x * cos - y * sin,
        y: ellipse.center.y + x * sin + y * cos,
      });
    };

    const start = at(curve.from);
    const end = at(curve.from + curve.sweep);

    /*
     * The large-arc flag has to be measured, not assumed. A sphere's rings are cut into halves
     * and it never mattered; a cone's base is not — it narrows toward the apex, so it hides
     * less than half its rim and the visible arc is the long way round. Written as the short
     * way it would cut across the figure as a lens shape.
     */
    const largeArc = curve.sweep > Math.PI ? 1 : 0;

    // The sweep flag is 0 because the y flip has already reversed the direction the parameter
    // runs in.
    return (
      `<path d="M ${num(start.x)} ${num(start.y)} ` +
      `A ${num(rx)} ${num(ry)} ${num(degrees(ellipse.rotation))} ${String(largeArc)} 0 ${num(end.x)} ${num(end.y)}" ` +
      `${attributes}/>`
    );
  };

  /*
   * Hidden first, so a visible curve or edge crossing one is drawn unbroken — the same ordering
   * the segment path uses, and for the same reason.
   */
  const byDepth = (hidden: boolean): readonly string[] => [
    ...solidElements(
      hidden ? [] : (straight?.visible ?? []),
      hidden ? (straight?.hidden ?? []) : [],
      to,
      style,
      frame.scale
    ),
    ...curves.filter((curve) => curve.hidden === hidden).map(elementFor),
  ];

  return [...byDepth(true), ...byDepth(false)];
}

function solidElements(
  visible: readonly Edge[],
  hidden: readonly Edge[],
  to: (point: Vec2) => Vec2,
  style: Style,
  scale: number
): readonly string[] {
  const edges = (list: readonly { a: Vec2; b: Vec2 }[]): string =>
    list
      .map((edge) => {
        const from = to(edge.a);
        const at = to(edge.b);
        return `M ${num(from.x)} ${num(from.y)} L ${num(at.x)} ${num(at.y)}`;
      })
      .join(' ');

  const result: string[] = [];

  // Hidden first, so a visible edge crossing one is drawn unbroken — the same ordering the
  // canvas painter uses, and for the same reason.
  if (hidden.length > 0) {
    result.push(
      `<path d="${edges(hidden)}" fill="none" stroke="${escapeAttribute(style.stroke)}" stroke-width="${num(Math.max(0.5, style.strokeWidth * 0.85) * scale)}" stroke-dasharray="${num(5 * scale)} ${num(4 * scale)}" opacity="${num(style.opacity * 0.45)}"/>`
    );
  }

  if (visible.length > 0) {
    result.push(`<path d="${edges(visible)}" ${paintAttributes(style, scale)}/>`);
  }

  return result;
}

/**
 * A label, laid out through the shared layout and emitted as positioned `tspan`s.
 *
 * Absolute `x`/`y` per run rather than relative `dx`/`dy`: relative offsets accumulate, so a
 * viewer that rounds differently drifts along the line, and a subscript ends up under the
 * wrong letter. Absolute positions cannot drift.
 */
/**
 * An angle mark: the square, or the arcs, plus the value.
 *
 * Uses the same geometry as the canvas painter, so the exported mark is the same shape and
 * in the same place. Two implementations of "where does the square go" would eventually
 * disagree, and the disagreement would only show up in a printed worksheet.
 */
function angleMarkElements(
  value: Extract<Evaluated, { kind: 'angle-mark' }>,
  to: (point: Vec2) => Vec2,
  style: Style,
  scale: number
): readonly string[] {
  const geometry = angleMarkGeometry(value.vertex, value.from, value.to, value.armLength, to, {
    nominalRadius: angleMarkNominal(value.showValue) * scale,
    size: value.size,
    valueOffset: value.valueOffset,
  });
  if (geometry === null) return [];

  /*
   * The dash is carried, unlike on the other symbols.
   *
   * An angle mark sits in the corner between two arms, and that corner can be inside a solid —
   * usually *is*, for the angles a problem names. `hiddenLines` has already put the answer in
   * the style; dropping it here would print a solid arc where the canvas drew a dashed one.
   */
  const dash = DASH[style.lineStyle];
  const stroke =
    `fill="none" stroke="${escapeAttribute(style.stroke)}" ` +
    `stroke-width="${num(Math.max(1, style.strokeWidth) * scale)}" ` +
    (dash.length > 0 ? `stroke-dasharray="${dash.map((d) => num(d * scale)).join(' ')}" ` : '') +
    `opacity="${num(style.opacity)}"`;

  const parts: string[] = [];

  if (value.isRight) {
    const [first, far, second] = rightAngleCorners(geometry);
    parts.push(
      `<path d="M ${num(first.x)} ${num(first.y)} L ${num(far.x)} ${num(far.y)} L ${num(second.x)} ${num(second.y)}" ${stroke}/>`
    );
  } else {
    for (const radius of tickRadii(geometry, value.ticks)) {
      parts.push(`<path d="${arcPath(geometry, radius)}" ${stroke}/>`);
    }
  }

  if (value.showValue) {
    const at = valuePosition(geometry, value.ticks);
    parts.push(
      `<text x="${num(at.x)}" y="${num(at.y)}" text-anchor="middle" dominant-baseline="middle" ` +
        `font-family="${LABEL_FONT}" font-size="${num(angleValueSize(style) * scale)}" ` +
        `fill="${escapeAttribute(style.stroke)}" opacity="${num(style.opacity)}">` +
        `${escapeText(formatDegrees(value.degrees))}°</text>`
    );
  }

  return parts;
}

/** One arc of a mark, as an SVG path. */
function arcPath(geometry: ReturnType<typeof angleMarkGeometry> & object, radius: number): string {
  const at = (angle: number) => ({
    x: geometry.vertex.x + Math.cos(angle) * radius,
    y: geometry.vertex.y + Math.sin(angle) * radius,
  });

  const start = at(geometry.startAngle);
  const end = at(geometry.endAngle);

  // The mark always takes the short way round, so the large-arc flag is never set.
  const sweepFlag = geometry.counterclockwise ? 0 : 1;
  return `M ${num(start.x)} ${num(start.y)} A ${num(radius)} ${num(radius)} 0 0 ${String(sweepFlag)} ${num(end.x)} ${num(end.y)}`;
}

/**
 * Text that belongs at a position it was given: a text entity, a measurement, an axis number.
 *
 * Distinct from {@link nameElement} on purpose. Those three are *placed* — the user dragged the
 * note, the measurement carries its own offset, the axis number sits under its tick — and
 * nudging them aside to avoid a line would move them off the thing they are pinned to. Only a
 * name is free to choose where it goes.
 */
function textElement(at: Vec2, content: string, style: Style, scale: number): string {
  return textFrom(at, content, style, scale);
}

/** An object's name, written in the direction the placement rule chose for it. */
function nameElement(
  at: Vec2,
  direction: Vec2 | undefined,
  content: string,
  style: Style,
  scale: number
): string {
  const fontPx = labelSize(style) * scale;
  const layout = layoutText(content, { fontPx, measure: EXPORT_MEASURER });
  if (layout.lines.length === 0) return '';

  // The gap is a screen-pixel quantity, so it scales with the export like the font does.
  const placed = namePlacement(
    direction ?? DEFAULT_NAME_DIRECTION,
    layout,
    fontPx,
    NAME_GAP * scale
  );
  return textFrom({ x: at.x + placed.dx, y: at.y + placed.dy }, content, style, scale);
}

/** The shared body: lay the string out and emit one `tspan` per run, starting at `origin`. */
/**
 * A single line of text centred on its point, for a table cell.
 *
 * Deliberately not routed through {@link textFrom}: that lays out scripts and line breaks
 * against a left origin, and a cell has neither. `text-anchor` and `dominant-baseline` do the
 * whole job here, exactly as they do for an angle mark's value.
 */
function centredTextElement(at: Vec2, content: string, style: Style, scale: number): string {
  if (content === '') return '';
  const size = labelSize(style) * scale;
  return (
    `<text x="${num(at.x)}" y="${num(at.y)}" text-anchor="middle" dominant-baseline="middle" ` +
    `font-family="${LABEL_FONT}" font-size="${num(size)}" ` +
    `fill="${escapeAttribute(style.stroke)}" opacity="${num(style.opacity)}">` +
    `${escapeText(content)}</text>`
  );
}

function textFrom(origin: Vec2, content: string, style: Style, scale: number): string {
  const fontPx = labelSize(style) * scale;
  const layout = layoutText(content, { fontPx, measure: EXPORT_MEASURER });
  if (layout.lines.length === 0) return '';

  const spans: string[] = [];
  layout.lines.forEach((line, index) => {
    const baseline = origin.y + index * layout.lineHeight;
    for (const run of line) {
      spans.push(
        `<tspan x="${num(origin.x + run.x)}" y="${num(baseline + run.y)}" font-size="${num(run.fontPx)}">${escapeText(run.text)}</tspan>`
      );
    }
  });

  return `<text font-family="${LABEL_FONT}" font-size="${num(fontPx)}" fill="${escapeAttribute(style.stroke)}" opacity="${num(style.opacity)}">${spans.join('')}</text>`;
}

/**
 * Widths for an export, which has no canvas to measure with.
 *
 * The approximation only decides where a subscript sits relative to the letter before it and
 * where a wrapped line breaks. Both are forgiving of a few per cent; what would not be
 * forgiving is a browser dependency in a function that must also run in a test.
 */
const EXPORT_MEASURER = approximateMeasurer();

/* -------------------------------------------------------------------- attributes */

const DASH: Readonly<Record<LineStyle, readonly number[]>> = {
  [LineStyle.Solid]: [],
  [LineStyle.Dashed]: [6, 4],
  [LineStyle.Dotted]: [1, 3],
};

function paintAttributes(style: Style, scale: number, fillable = false): string {
  const parts = [
    `stroke="${escapeAttribute(style.stroke)}"`,
    `stroke-width="${num(style.strokeWidth * scale)}"`,
    'stroke-linejoin="round"',
    'stroke-linecap="round"',
  ];

  const dash = DASH[style.lineStyle];
  if (dash.length > 0) {
    parts.push(`stroke-dasharray="${dash.map((d) => num(d * scale)).join(' ')}"`);
  }

  /*
   * Only a closed shape can be filled. Applying a fill to an open path would have SVG close
   * it implicitly and paint a region the canvas never showed — the two would disagree, and
   * the SVG would be the wrong one.
   */
  const filled = fillable && style.fillStyle !== 'none' && style.fill !== 'transparent';
  /*
   * Hatching is a **pattern**, not a colour, and until now this line emitted the colour for both.
   * A shape hatched on screen exported as a flat wash — the panel offered the choice, the canvas
   * honoured it, and the file quietly did not. `STYLE_PRESETS.plane` says in as many words that a
   * plane is toned rather than hatched *because* "the SVG and PDF writers treat every fill as
   * solid"; that was true, and this is the half of it that is worth fixing rather than living with.
   *
   * A `<pattern>` rather than a clip and a run of lines, which is what the canvas does: an SVG
   * file has no panning to make the stripes crawl, and a pattern keeps the emitted path a path.
   */
  parts.push(
    `fill="${filled ? (style.fillStyle === 'hatch' ? `url(#${hatchId(style.fill)})` : escapeAttribute(style.fill)) : 'none'}"`
  );

  if (style.opacity !== 1) parts.push(`opacity="${num(style.opacity)}"`);

  return parts.join(' ');
}

/* ------------------------------------------------------------------------ helpers */

function pathData(points: readonly Vec2[], closed: boolean): string {
  const parts: string[] = [];
  points.forEach((point, index) => {
    parts.push(`${index === 0 ? 'M' : 'L'} ${num(point.x)} ${num(point.y)}`);
  });
  if (closed) parts.push('Z');
  return parts.join(' ');
}

/**
 * Clips a line or ray to the export frame.
 *
 * Reuses the canvas painter's clipper rather than repeating it. An export frame and a
 * viewport are the same problem — a rectangle an endless object has to be cut down to — and
 * two implementations would eventually disagree about the corners.
 */
function clipUnbounded(value: Evaluated, frame: ExportFrame): { a: Vec2; b: Vec2 } | null {
  if (value.kind === 'line') return clipLineToBounds(value.line, frame.world);
  if (value.kind === 'ray') return clipRayToBounds(value.ray, frame.world);
  return null;
}

/** Three decimals: below a thousandth of a pixel nothing is visible, and the file is smaller. */
function num(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 1000) / 1000;
  return String(rounded === 0 ? 0 : rounded);
}

function escapeText(value: string): string {
  return value.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
}

function escapeAttribute(value: string): string {
  return escapeText(value).replace(/"/g, '&quot;');
}

/**
 * A filled arrowhead, as a triangle.
 *
 * A `marker` would be the idiomatic SVG, and it is avoided for the reason recorded on the
 * vector arrowhead above: a marker inherits the path's own stroke, so a dashed axis would come
 * out with a dashed arrow. A plain polygon is three points and behaves.
 */
function arrowheadElement(from: Vec2, to: Vec2, style: Style, scale: number): string {
  const away = { x: to.x - from.x, y: to.y - from.y };
  const length = Math.hypot(away.x, away.y);
  if (length < 1e-9) return '';

  const direction = { x: away.x / length, y: away.y / length };
  const across = { x: -direction.y, y: direction.x };

  const size = 9 * scale;
  const half = 3.5 * scale;
  const base = { x: to.x - direction.x * size, y: to.y - direction.y * size };

  const points = [
    `${num(to.x)},${num(to.y)}`,
    `${num(base.x + across.x * half)},${num(base.y + across.y * half)}`,
    `${num(base.x - across.x * half)},${num(base.y - across.y * half)}`,
  ].join(' ');

  return `<polygon points="${points}" fill="${escapeAttribute(style.stroke)}"/>`;
}
